import { useEffect, useState, useCallback } from "react";
import { ClipboardList, Package, Trash2, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { ProcurementHistoryDialog } from "@/components/products/ProcurementHistoryDialog";
import BuyerItemFields from "@/components/procurement/BuyerItemFields";

const IMG_BASE = process.env.REACT_APP_BACKEND_URL || "";

/**
 * 2026-06 · 采购 in-app view of a quote's own products. Same editing rights as
 * the 采购报价要求 link: manage 采购报价 records + import them into 价格明细栏.
 */
export default function BuyerQuoteItems({ quoteId, onItemsChange }) {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [history, setHistory] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/quotes/${quoteId}/buyer-items`);
      setData(data);
    } catch (e) {
      setData({ items: [] });
      console.warn("buyer items load failed:", e);
    }
  }, [quoteId]);

  useEffect(() => { load(); }, [load]);

  /** 2026-09 · 起订量必填 (user spec) — 采购必须先填起订量才能填报/导入/完成报价。 */
  const requireMoqOk = (item) => {
    if (String(item?.moq || "").trim()) return true;
    toast.error(t("procReq.moqRequired"));
    return false;
  };

  const openQuoteForm = async (item) => {
    const key = item.buyer_item_number || item.item_number;
    let before = 0;
    try {
      const { data } = await api.get("/procurement-quotes", { params: { item_number: key, page_size: 1 } });
      before = data.total || 0;
    } catch { /* best-effort count */ }
    setHistory({ itemNumber: key, before });
  };

  const closeHistory = async () => {
    const ctx = history;
    setHistory(null);
    if (!ctx) return;
    try {
      const { data } = await api.get("/procurement-quotes", { params: { item_number: ctx.itemNumber, page_size: 1 } });
      if ((data.total || 0) - ctx.before > 0) {
        toast.success(t("procReq.submitted"));
        load();
      }
    } catch { /* best-effort */ }
  };

  const finishQuoting = async (item) => {
    try {
      await api.post(`/quotes/${quoteId}/buyer-items/${encodeURIComponent(item.item_number)}/done`);
      toast.success(t("procReq.markedDone"));
      setData((d) => ({ ...d, items: (d.items || []).filter((i) => i.item_number !== item.item_number) }));
      onItemsChange?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteRow = async (item, row) => {
    try {
      await api.delete(`/quotes/${quoteId}/buyer-items/${encodeURIComponent(item.item_number)}/rows/${row.id}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (!data) return <div className="p-6 text-center text-muted-foreground text-sm" data-testid="buyer-items-loading">…</div>;
  return (
    <div className="space-y-4" data-testid="buyer-quote-items">
      {data.items.length === 0 && (
        <div className="bg-card border border-border rounded-md p-8 text-center text-muted-foreground text-sm"
          data-testid="buyer-quote-items-empty">{t("procReq.empty")}</div>
      )}
      {data.items.map((item) => (
        <div key={item.item_number} className="bg-card border border-border rounded-md p-4 flex flex-col md:flex-row gap-4"
          data-testid={`buyer-item-${item.item_number}`}>
          <div className="w-full md:w-36 h-36 shrink-0 rounded-md border border-border bg-secondary/20 flex items-center justify-center overflow-hidden">
            {item.photo_url ? (
              <img src={`${IMG_BASE}${item.photo_url}`} alt={item.item_number} className="w-full h-full object-contain" />
            ) : (
              <Package size={26} className="text-muted-foreground" />
            )}
          </div>
          <div className="flex-1 min-w-0 space-y-2">
            <div className="flex items-start justify-between gap-3 flex-col sm:flex-row">
              <div className="min-w-0">
                <p className="font-mono text-sm font-bold text-brand">{item.buyer_item_number || item.item_number}</p>
                <p className="text-sm">{item.description || "-"}</p>
                <p className="text-[11px] text-amber-600 font-medium" data-testid={`buyer-item-status-${item.item_number}`}>
                  {t("procReq.notDoneYet")}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0 w-full sm:w-auto">
              <Button size="sm" onClick={() => { if (!requireMoqOk(item)) return; openQuoteForm(item); }}
                data-testid={`buyer-item-quote-button-${item.item_number}`}
                className="rounded-md bg-brand hover:bg-brand-hover shrink-0 flex-1 sm:flex-none h-10 sm:h-9">
                <ClipboardList size={14} /> {t("procReq.fileQuote")}
              </Button>
              {/* 2026-06 · 完成报价 — the row leaves this list afterwards. */}
              <Button size="sm" variant="outline" onClick={() => { if (!requireMoqOk(item)) return; finishQuoting(item); }}
                data-testid={`buyer-item-done-button-${item.item_number}`}
                className="rounded-md shrink-0 flex-1 sm:flex-none h-10 sm:h-9 border-emerald-500/60 text-emerald-700 hover:bg-emerald-50">
                <CheckCircle2 size={14} /> {t("procReq.markDone")}
              </Button>
            </div>
            </div>
            {item.specification && (
              <div>
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{t("quote.editor.specMaterial")}</p>
                <p className="text-xs font-mono whitespace-pre-wrap">{item.specification}</p>
              </div>
            )}
            <BuyerItemFields
              item={item}
              requireMoq
              onSave={async (fields) => {
                try {
                  await api.patch(`/quotes/${quoteId}/buyer-items/${encodeURIComponent(item.item_number)}`, fields);
                  // Keep the local copy in sync so the 起订量 required check sees the new value.
                  setData((d) => (!d ? d : {
                    ...d,
                    items: d.items.map((it) => (it.item_number === item.item_number ? { ...it, ...fields } : it)),
                  }));
                } catch (e) {
                  toast.error(formatApiErrorDetail(e.response?.data?.detail));
                }
              }}
            />
            {item.procurement_note && (
              <div className="rounded-md border border-brand/30 bg-brand/5 px-3 py-2">
                <p className="text-[10px] uppercase tracking-wide text-brand font-semibold">{t("quote.editor.procurementNote")}</p>
                <p className="text-xs whitespace-pre-wrap">{item.procurement_note}</p>
              </div>
            )}
            <div className="space-y-1">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">{t("procReq.priceRows")}</p>
              {/* 2026-06 · 手机端：价格明细改卡片 + 「报价 / 装箱」标签切换 */}
              <PriceRowsMobile item={item} onDelete={(r) => deleteRow(item, r)} />
              <div className="hidden md:block overflow-x-auto border border-border rounded-md">
                <table className="w-full text-xs" data-testid={`buyer-item-rows-${item.item_number}`}>
                  <thead className="bg-secondary/40">
                    <tr>
                      {[t("quote.size"), t("procReq.factoryCost"), t("procReq.qtyPerCtn"),
                        t("procReq.caseSize"), t("procReq.containerQty"), ""].map((h, i) => (
                        <th key={h || `x${i}`} className="px-2 py-1.5 text-left font-semibold text-[10px] uppercase tracking-wide text-muted-foreground whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(item.price_rows || []).length === 0 && (
                      <tr><td colSpan={6} className="px-2 py-3 text-center text-muted-foreground">{t("procReq.noRows")}</td></tr>
                    )}
                    {(item.price_rows || []).map((r) => (
                      <tr key={r.id} className="border-t border-border/60">
                        <td className="px-2 py-1.5 font-mono whitespace-nowrap">{r.size || "-"}</td>
                        <td className="px-2 py-1.5 font-mono">{r.factory_cost ? `${r.factory_cost} ${r.cost_currency || ""}` : "-"}</td>
                        <td className="px-2 py-1.5 font-mono">{r.qty_per_ctn || "-"}</td>
                        <td className="px-2 py-1.5 font-mono whitespace-nowrap">
                          {r.case_length ? `${r.case_length}×${r.case_width}×${r.case_height}` : "-"}
                        </td>
                        <td className="px-2 py-1.5 font-mono">{r.container_qty_40 || "-"}</td>
                        <td className="px-1 py-1.5 text-right">
                          {r.can_delete && (
                            <button
                              onClick={() => deleteRow(item, r)}
                              className="p-1 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                              title={t("procReq.deleteRow")}
                              data-testid={`buyer-row-delete-${r.id}`}
                            >
                              <Trash2 size={13} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      ))}

      <ProcurementHistoryDialog
        open={Boolean(history)}
        onOpenChange={(o) => { if (!o) closeHistory(); }}
        buyerItemNumber={history?.itemNumber}
        onImport={async (rows) => {
          if (!rows?.length) return;
          const target = (data?.items || []).find((it) => (it.buyer_item_number || it.item_number) === history?.itemNumber);
          if (target && !requireMoqOk(target)) return;
          try {
            await api.post(`/quotes/${quoteId}/buyer-items/${encodeURIComponent(history.itemNumber)}/rows`, { rows });
            toast.success(t("procReq.imported").replace("{n}", String(rows.length)));
            setHistory(null);
            load();
          } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
          }
        }}
      />
    </div>
  );
}

/**
 * 2026-06 · 手机端价格明细卡片 —「报价 / 装箱」两个标签切换（用户要求）。
 */
const PriceRowsMobile = ({ item, onDelete }) => {
  const { t } = useLanguage();
  const [tab, setTab] = useState("price");
  const rows = item.price_rows || [];

  const fields = tab === "price"
    ? [[t("procReq.factoryCost"), (r) => (r.factory_cost ? `${r.factory_cost} ${r.cost_currency || ""}` : "-")],
       [t("procReq.moq"), () => item.moq || "-"]]
    : [[t("procReq.qtyPerCtn"), (r) => r.qty_per_ctn || "-"],
       [t("procReq.caseSize"), (r) => (r.case_length ? `${r.case_length}×${r.case_width}×${r.case_height}` : "-")],
       [t("procReq.containerQty"), (r) => r.container_qty_40 || "-"]];

  return (
    <div className="md:hidden space-y-2" data-testid={`buyer-item-rows-mobile-${item.item_number}`}>
      <div className="flex rounded-md border border-border overflow-hidden">
        {[["price", t("procReq.tabPrice")], ["packing", t("procReq.tabPacking")]].map(([key, label]) => (
          <button key={key} type="button" onClick={() => setTab(key)}
            className={`flex-1 h-9 text-xs font-semibold transition-colors ${tab === key ? "bg-brand text-white" : "bg-card text-muted-foreground"}`}
            data-testid={`buyer-rows-tab-${key}-${item.item_number}`}>
            {label}
          </button>
        ))}
      </div>
      {rows.length === 0 && (
        <p className="text-center text-xs text-muted-foreground py-4">{t("procReq.noRows")}</p>
      )}
      {rows.map((r) => (
        <div key={r.id} className="rounded-md border border-border p-2.5" data-testid={`buyer-row-card-${r.id}`}>
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm font-bold">{r.size || "-"}</span>
            {r.can_delete && (
              <button onClick={() => onDelete(r)} className="ml-auto p-1.5 rounded-md text-muted-foreground active:text-destructive"
                data-testid={`buyer-row-mobile-delete-${r.id}`}>
                <Trash2 size={14} />
              </button>
            )}
          </div>
          <div className="mt-1 grid grid-cols-2 gap-x-2 gap-y-0.5 text-[11px]">
            {fields.map(([label, get]) => (
              <div key={label} className="flex gap-1 min-w-0">
                <span className="text-muted-foreground shrink-0">{label}:</span>
                <span className="font-mono truncate">{get(r)}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
